import { CASE_STUDIES } from "../data/landing";
import { motion } from "framer-motion";
import AnimatedNumber from "../components/ui/AnimatedNumber";

const EASE = [0.22, 1, 0.36, 1];
const CARD_REVEAL = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};
const cardTransition = (i) => ({ duration: 0.55, delay: (i % 3) * 0.08, ease: EASE });

export default function CaseStudy() {
  return (
    <section
      id="case-study"
      className="relative bg-[#050B26] py-24 sm:py-32 overflow-hidden"
      data-testid="case-study-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CaseStudyHeader />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CASE_STUDIES.map((c, i) => (
            <CaseStudyCard key={c.client} study={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CaseStudyHeader() {
  return (
    <div className="max-w-3xl mx-auto text-center mb-16 sm:mb-20">
      <p className="text-[11px] uppercase tracking-[0.3em] text-[#5b80ff] font-semibold mb-5">
        Case Study
      </p>
      <h2 className="font-arial-black text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight">
        Receipts, not promises.
        <span className="block text-white/55">Nine brands. Real outcomes.</span>
      </h2>
      <div className="mx-auto mt-7 h-px w-16 bg-[#1440E1]" aria-hidden="true" />
      <p className="mt-7 text-white/80 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
        Snapshots of campaigns we&rsquo;ve shipped — measurable, repeatable,
        and built to compound long after launch day.
      </p>
    </div>
  );
}

function CaseStudyCard({ study, index }) {
  return (
    <motion.article
      {...CARD_REVEAL}
      transition={cardTransition(index)}
      className="case-card group relative overflow-hidden cursor-pointer"
      data-testid={`case-study-card-${index}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={study.image}
          alt={study.client}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-4 left-4 bg-[#1440E1] text-white px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
          {study.industry}
        </div>
      </div>
      <div className="p-5 sm:p-6 bg-white border border-neutral-200 border-t-0 group-hover:bg-[#1440E1]/[0.04] group-hover:border-[#1440E1]/40 transition-colors">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#1440E1] font-semibold mb-2">
          {study.client}
        </p>
        <p className="text-[#050B26] font-medium text-sm sm:text-base leading-snug mb-5">
          {study.headline}
        </p>
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-neutral-200">
          <Metric value={study.metrics.engagement} label="Engagement" testId={`case-metric-engagement-${index}`} />
          <Metric value={study.metrics.followers} label="Monthly Followers" testId={`case-metric-followers-${index}`} />
          <Metric value={study.metrics.reach} label="Monthly Reach" testId={`case-metric-reach-${index}`} />
        </div>
      </div>
    </motion.article>
  );
}

function Metric({ value, label, testId }) {
  return (
    <div className="flex flex-col" data-testid={testId}>
      <AnimatedNumber
        value={value}
        className="font-display text-base sm:text-lg font-extrabold text-[#050B26] tracking-tight leading-none tabular-nums"
        testId={`${testId}-value`}
      />
      <span className="text-[9px] uppercase tracking-[0.18em] text-[#050B26]/55 mt-1.5 leading-tight">
        {label}
      </span>
    </div>
  );
}
